import { useRef, useState, useCallback, useEffect } from 'react';
import { createSession, saveSnapshot, endSession, saveDailySummary } from '../firebase';
import { computeDailySummary, computeExtendedSummary } from '../engine/metrics';

const LOCAL_KEY = 'moodscope_snapshots_v1';

function loadFromLocal() {
  try {
    const saved = JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]');
    const today = new Date().toDateString();
    return saved.filter(s => new Date(s.timestamp).toDateString() === today);
  } catch(e) { return []; }
}

export function useSession(user) {
  const sessionIdRef    = useRef(null);
  const lastFirebaseRef = useRef(Date.now());
  const pendingBatchRef = useRef([]);

  // ── Initialise from localStorage immediately (before any tracking starts) ──
  const restored = loadFromLocal();
  const snapshotsRef    = useRef(restored);
  const [sessionId,     setSessionId]     = useState(null);
  const [snapshotCount, setSnapshotCount] = useState(restored.length);
  const [saveError,     setSaveError]     = useState(null);

  // ── Persist to localStorage ───────────────────────────────────────────────
  const persistLocal = useCallback((snapshots) => {
    try {
      localStorage.setItem(LOCAL_KEY, JSON.stringify(snapshots.slice(-500)));
    } catch(e) {}
  }, []);

  const startSession = useCallback(async () => {
    // Re-hydrate from localStorage in case more was written from another tab
    const fresh = loadFromLocal();
    snapshotsRef.current = fresh;
    pendingBatchRef.current = [];
    setSnapshotCount(fresh.length);

    if (!user) return;
    try {
      const device = navigator.userAgent.includes('Mobile') ? 'mobile' : 'desktop';
      const id = await createSession(user.uid, device);
      sessionIdRef.current = id;
      setSessionId(id);
      setSaveError(null);
    } catch(e) {
      console.warn('Session create failed:', e);
    }
  }, [user]);

  // ── Called every minute by the detection hook ─────────────────────────────
  const handleSnapshot = useCallback(async (snapshot) => {
    snapshotsRef.current.push(snapshot);
    pendingBatchRef.current.push(snapshot);
    persistLocal(snapshotsRef.current);
    setSnapshotCount(snapshotsRef.current.length);

    // Flush to Firebase every 30 minutes
    const now = Date.now();
    if (now - lastFirebaseRef.current >= 1800000 && user && sessionIdRef.current && pendingBatchRef.current.length > 0) {
      const batch = [...pendingBatchRef.current];
      pendingBatchRef.current = [];
      lastFirebaseRef.current = now;
      batch.forEach(snap => saveSnapshot(user.uid, sessionIdRef.current, snap).catch(() => {}));
    }
  }, [user, persistLocal]);

  const finishSession = useCallback(async () => {
    if (user && sessionIdRef.current && pendingBatchRef.current.length > 0) {
      const batch = [...pendingBatchRef.current];
      pendingBatchRef.current = [];
      for (const snap of batch) {
        await saveSnapshot(user.uid, sessionIdRef.current, snap).catch(() => {});
      }
    }
    if (user && sessionIdRef.current) {
      try { await endSession(user.uid, sessionIdRef.current); } catch(e) {}
    }
    const snapshots = snapshotsRef.current;
    if (snapshots.length > 0) {
      const summary  = computeDailySummary(snapshots);
      const extended = computeExtendedSummary(snapshots);
      if (summary && user) {
        const dateStr = new Date().toISOString().slice(0, 10);
        const fullSummary = {
          ...summary,
          // Extended fields for baseline personalisation
          flowMinutes:      extended?.flowMinutes      ?? 0,
          sessionQuality:   extended?.sessionQuality   ?? null,
          cognitiveLoad:    extended?.cognitiveLoad    ?? null,
          emotionStability: extended?.emotionStability ?? null,
        };
        try { await saveDailySummary(user.uid, dateStr, fullSummary); } catch(e) {}
      }
    }
    sessionIdRef.current = null;
    setSessionId(null);
  }, [user]);

  const getSnapshots = useCallback(() => snapshotsRef.current, []);

  const clearLocal = useCallback(() => {
    snapshotsRef.current = [];
    pendingBatchRef.current = [];
    setSnapshotCount(0);
    try { localStorage.removeItem(LOCAL_KEY); } catch(e) {}
  }, []);

  return { sessionId, snapshotCount, saveError, startSession, handleSnapshot, finishSession, getSnapshots, clearLocal };
}
